import type { ThreadUpdateEvent } from "@/lib/use-visitor-threads-realtime";
import { htmlToPlainText, type NotificationLabels } from "./notification-text";

export interface AnnounceContext {
  playInbound: () => void;
  /** Tapping the launcher peek reopens the thread this message landed on. */
  markPeekTarget: () => void;
  /** Home `recent-message` block cache write (the anchor chat only; the caller decides). */
  writeRecent: () => void;
  notify: {
    /** The visitor's desktop-notification preference. */
    enabled: boolean;
    /** The visitor is reading THAT thread right now: the popup would be redundant. */
    reading: boolean;
    title: string;
    labels: NotificationLabels;
    orgLogoUrl?: string | null;
    /** Click: focus the tab, open the panel on the thread. */
    open: () => void;
  };
}

/**
 * Everything a NEW inbound message triggers, from one `threadUpdated` content frame (the caller has
 * already decided it is new, see `takeAnnouncement`): the sound, the peek target, the Home block, and the
 * desktop notification. The one place these live, so they work with the panel closed, the tab hidden and
 * on any route, without a per-chat stream.
 */
export function announceThreadFrame(
  e: Pick<
    ThreadUpdateEvent,
    | "threadId"
    | "lastMessageId"
    | "lastMessageAt"
    | "lastMessagePreview"
    | "lastMessageSenderAvatar"
  >,
  ctx: AnnounceContext,
): void {
  ctx.playInbound();
  ctx.markPeekTarget();
  ctx.writeRecent();

  const { notify } = ctx;
  const permitted = typeof Notification !== "undefined" && Notification.permission === "granted";
  if (!notify.enabled || !permitted || notify.reading) return;
  try {
    const n = new Notification(notify.title || notify.labels.newMessage, {
      body: htmlToPlainText(e.lastMessagePreview ?? "", [], notify.labels),
      tag: e.lastMessageId ?? `${e.threadId}@${e.lastMessageAt ?? ""}`,
      icon: e.lastMessageSenderAvatar ?? notify.orgLogoUrl ?? undefined,
    });
    n.onclick = () => {
      try {
        window.focus();
      } catch {
        /* sandboxed contexts disallow focus; ignore */
      }
      notify.open();
      n.close();
    };
  } catch (err) {
    // The Notification API can throw on iOS web / SecurityError contexts.
    console.warn("[wexio] desktop notification failed:", err);
  }
}
